import PropTypes from 'prop-types';
import React, { useCallback, useRef, useEffect } from 'react';
import { Popup, FeatureGroup } from 'react-leaflet';
import RotatedMarker from './RotatedMarker';
import AntPath from "react-leaflet-ant-path";
import AisPopupContent from './AisPopupContent';
import _shipGreyIcon from "./img/targetShipGray.png";
import _shipBlueIcon from "./img/targetShipBlue.png";
import "./map.css";

const L = window.L;
const TRAIL_OPTIONS = { color: 'blue', weight: 1, delay: 2000, dashArray: '4, 10' };
const iconCache = {};

function getIcon(type, isSelectedAis) {
  const cacheKey = `${type}:${isSelectedAis ? 'selected' : 'default'}`;

  if (iconCache[cacheKey]) {
    return iconCache[cacheKey];
  }

    let icon;

    switch (type) {
        case "ship":
            icon = isSelectedAis ? _shipBlueIcon : _shipGreyIcon;
            break;

        default:
            icon = isSelectedAis ? _shipBlueIcon : _shipGreyIcon;
            break;
    }

        iconCache[cacheKey] = L.icon({
        iconUrl: icon,
        iconSize: [16, 28],
    });

        return iconCache[cacheKey];
}

const RotatedAis = ({ data, aisType, isSelectedAis, onAisSelected }) => {
  const popupRef = useRef(null);

  const handlePopupOpen = useCallback(() => {
    if (onAisSelected && data?.mmsi !== undefined && !isSelectedAis) {
      onAisSelected(data.mmsi);
    }
  }, [onAisSelected, data?.mmsi, isSelectedAis]);

  // Defensive: close popup if data becomes invalid
  useEffect(() => {
    const valid =
      typeof data?.lat === 'number' &&
      typeof data?.lon === 'number' &&
      !isNaN(data.lat) &&
      !isNaN(data.lon);

    if (!valid && popupRef.current && popupRef.current._map) {
      try {
        popupRef.current._close(); // v2 internal, safe fallback in your codebase
      } catch (e) {}
    }
  }, [data]);

  const valid =
    typeof data?.lat === 'number' &&
    typeof data?.lon === 'number' &&
    !isNaN(data.lat) &&
    !isNaN(data.lon);

  if (!valid) return null;

  return (
    <RotatedMarker
      rotationAngle={typeof data.cog === 'number' ? data.cog : 0}
      rotationOrigin="center"
      position={[data.lat, data.lon]}
      icon={getIcon(aisType, isSelectedAis)}
      // ✅ Leaflet-native events (wired up by RotatedMarker.setupMarker)
      eventHandlers={{
        mouseover: (e) => e.target.openPopup(),
        mouseout: (e) => e.target.closePopup(),
        click: (e) => e.target.openPopup(), // keep click-to-open too
      }}
    >
      <Popup ref={popupRef} onOpen={handlePopupOpen}>
        <AisPopupContent data={data} />
      </Popup>
    </RotatedMarker>
  );
};
const Ais = React.memo(({ data, isSelectedAis = false, aisType = 'ship', onAisSelected }) => {
    return (
        <FeatureGroup>
            <RotatedAis
                data={data}
                aisType={aisType}
                isSelectedAis={isSelectedAis}
                onAisSelected={onAisSelected}
            />
        </FeatureGroup>
    );
});

Ais.propTypes = {
    data: PropTypes.object.isRequired,
    isSelectedAis: PropTypes.bool,
    aisType: PropTypes.string,
    onAisSelected: PropTypes.func,
};

Ais.defaultProps = {
    isSelectedAis: false,
    aisType: 'ship',
    onAisSelected: () => { }, // Provide a no-op function as a default to avoid potential errors
};

const AisList = ({ ais, selectedAis, selectedAisTrail, onAisSelected }) => {

    if (!ais || ais.length === 0) {
        return null; // Early return for empty or undefined `ais`
    }

    const validTrail = Array.isArray(selectedAisTrail) && selectedAisTrail.length > 1 && selectedAisTrail.every(
      point => Array.isArray(point) && point.length === 2 && point.every(Number.isFinite)
    );

    const items = ais.map(aisTarget => (
        <Ais
            key={aisTarget.mmsi}
            data={aisTarget}
            isSelectedAis={selectedAis === aisTarget.mmsi}
            onAisSelected={onAisSelected}
        />
    ));

    return (
      <>
        {items}
        {selectedAis != null && validTrail && (
          <AntPath
            positions={selectedAisTrail}
            options={TRAIL_OPTIONS}
          />
        )}
      </>
    );
};


AisList.propTypes = {
    ais: PropTypes.array,
    selectedAis: PropTypes.number,
    selectedAisTrail: PropTypes.array,
    onAisSelected: PropTypes.func
};


export default AisList;
